import { NgClass } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbPagination } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { ApiService } from 'services/api.service';
import { NotificationService } from 'services/notification.service';
import { FilterType, TableColumn, TableFilters } from 'types/table-column.interface';
import { formatDatetime } from 'util/date.utility';
import { formatIdentityNumber } from 'util/user.utility';
import { DateRangePickerComponent } from '../date-range-picker/date-range-picker.component';
import { TimeRangePickerComponent } from 'components/time-range-picker/time-range-picker.component';
import { AuditEntryModalComponent } from 'components/modal/audit-entry-modal/audit-entry-modal.component';
import { Subject, takeUntil } from 'rxjs';

const HEADER_PADDING = 12;

export const ANIMATION_TIMEOUT_700 = 700;
export const ANIMATION_TIMEOUT_200 = 200;
export const TIME_FORMAT = 'HH:mm';

@Component({
  selector: 'app-table',
  standalone: true,
  imports: [
    TranslateModule,
    SpinnerComponent,
    NgClass,
    FormsModule,
    NgbPagination,
    DateRangePickerComponent,
    TooltipComponent,
    TimeRangePickerComponent,
    AuditEntryModalComponent
  ],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss'
})
export class TableComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  public readonly formatDate = formatDatetime;
  public readonly formatIdentityNumber = formatIdentityNumber;

  @ViewChild('tableBody') tableBody!: ElementRef;
  @ViewChild('tableWrapper') tableWrapper!: ElementRef;
  @ViewChild('loader') loader!: ElementRef;

  @Input() columns: TableColumn[] = [];
  @Input() actionColumnStyleClass = '';
  @Input() endpoint: string = '';
  @Input() tableTooltipText: string = '';
  @Input() getViewPathFn?: (row: any) => string;
  @Input() localStorageKey: string = '';
  @Input() viewEntryPath = '';
  @Input() editEntryPath = '';
  @Input() shouldDefaultSort: boolean = true;
  @Input() rowActions: string[] = [];
  @Input() sortBy: string = 'name';
  @Input() sortDirection: 'asc' | 'desc' = 'asc';
  @Input() isBackofficeUserList: boolean = false;
  @Input() withSearch: boolean = true;
  @Input() type: string = 'default';
  @Input() withPagination: boolean = true;
  @Input() flatTopBorder: boolean = false;
  @Input() useLocalSrorage: boolean = false;
  @Input() clientId: string | null = null;
  @Input() showTooltipText: boolean = false;
  @Input() isEntryPathForSubmittion: boolean = false;
  @Input() defaultFilters: { [key: string]: string | string[] } = {};
  @Input() initialColumnFilters: { [key: string]: string } = {};
  @Input() showEmpty: boolean = false;
  @Input() linkRow: boolean = true;
  @Input() draftsFirstKey: string = '';
  @Input() responseKey: string = '';
  @Input() topFilterOptions: string[] = [];
  @Input() topFilterTranslatePrefix: string = '';
  @Input() topFilterCounts: { status: string; count: number }[] = [];
  @Input() topFilterParamName: string = '';
  @Input() topFilterDefaultSelected: string[] = [];
  @Input() topFilterAlwaysIncluded: string[] = [];
  @Output() responseLoaded = new EventEmitter<any>();
  @Output() customActionClicked = new EventEmitter<{ action: string; row: any }>();
  images: { [key: string]: string } = {};
  topFilterSelected: Set<string> = new Set();

  searchValue: string = '';
  pagedResult: any = {};
  isMainDataLoading: boolean = true;
  isAdditionalDataLoading: boolean = true;
  tableFilters: TableFilters = {};
  pageSize: number = 10;
  page: number = 1;
  tableBodyHeight: number = 0;
  selectedAuditEntry: any = null;
  isAuditEntryModalOpen: boolean = false;
  private dynamicFilterOptionsAccumulator: Map<string, Set<string>> = new Map();

  get isLoading(): boolean {
    return this.isMainDataLoading || this.isAdditionalDataLoading;
  }

  constructor(
    private apiService: ApiService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.columns.forEach((column: TableColumn) => {
      this.tableFilters[column.sortKey || column.key] = {
        isOpen: false,
        value: '',
        resetTrigger: false
      };
      if (column.dateFilterKey) {
        this.tableFilters[column.dateFilterKey] = {
          isOpen: false,
          value: '',
          resetTrigger: false
        };
      }
    });

    if (Object.keys(this.initialColumnFilters).length) {
      const existingParams = this.activatedRoute.snapshot.queryParams;
      const hasExistingFilters = Object.keys(existingParams).some((k) => k.startsWith('filter.'));
      if (!hasExistingFilters) {
        Object.entries(this.initialColumnFilters).forEach(([key, value]) => {
          if (this.tableFilters[key]) {
            this.tableFilters[key].value = value;
          }
        });
      }
    }

    if (this.topFilterOptions.length) {
      this.topFilterSelected = new Set(this.topFilterDefaultSelected.length ? this.topFilterDefaultSelected : this.topFilterOptions);
      this.syncTopFilterToColumnFilter();
    }

    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      if (params['sortBy.by']) {
        this.sortBy = params['sortBy.by'];
        this.sortDirection = params['sortBy.desc']?.toLowerCase() == 'true' ? 'desc' : 'asc';
      } else {
        this.sortBy = this.sortBy || 'id';
        this.sortDirection = this.sortDirection || 'desc';
      }

      if (params['page']) {
        this.page = params['page'];
      }

      if (params['pageSize']) {
        this.pageSize = params['pageSize'];
      } else {
        this.pageSize = 10;
      }
      this.getTableData();
    });

    if (!this.withPagination) {
      this.pageSize = 1000;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getTableData(): void {
    if (this.topFilterParamName && this.topFilterSelected.size === 0) {
      this.pagedResult = { list: [], totalCount: 0 };
      this.isMainDataLoading = false;
      this.isAdditionalDataLoading = false;
      return;
    }

    if (this.showEmpty) {
      this.pagedResult = {
        list: [],
        totalCount: 0
      };
      this.isMainDataLoading = false;
      this.isAdditionalDataLoading = false;
    } else if (this.endpoint.length) {
      let params = this.getQueryParamsAsString();

      // When topFilter manages a param, strip it from URL-derived params to avoid duplication
      if (this.topFilterParamName) {
        const urlParams = new URLSearchParams(params);
        urlParams.delete(`filter.${this.topFilterParamName}`);
        params = urlParams.toString();
      }

      if (!params.includes('sortBy.by')) {
        params += `&sortBy.by=${this.sortBy}`;
      }

      if (!params.includes('sortBy.desc')) {
        params += `&sortBy.desc=${this.sortDirection === 'desc'}`;
      }

      if (this.clientId) {
        if (!params.length) {
          params += `filter.clientId=${this.clientId}`;
        } else {
          params += `&filter.clientId=${this.clientId}`;
        }
      }

      if (this.defaultFilters) {
        Object.entries(this.defaultFilters).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((v) => {
              params += params.length ? '&' : '';
              params += `filter.${key}=${encodeURIComponent(v)}`;
            });
          } else {
            params += params.length ? '&' : '';
            params += `filter.${key}=${encodeURIComponent(value)}`;
          }
        });
      }

      // Apply initial column filters if not already in URL params
      for (const [filterKey, initialValue] of Object.entries(this.initialColumnFilters)) {
        const column = this.columns.find((c) => (c.sortKey || c.key) === filterKey);
        if (!column?.multiselectFilterName) continue;
        const paramKey = `filter.${column.multiselectFilterName}`;
        if (params.includes(paramKey)) continue;
        const filterValue = this.tableFilters[filterKey]?.value;
        if (!filterValue) continue;
        const selectedValues = filterValue.split('|').filter((v: string) => !!v);
        selectedValues.forEach((v: string) => {
          params += params.length ? '&' : '';
          params += `${paramKey}=${encodeURIComponent(v)}`;
        });
      }

      if (this.topFilterParamName && this.topFilterSelected.size > 0) {
        [...this.topFilterSelected, ...this.topFilterAlwaysIncluded].forEach((status) => {
          params += params.length ? '&' : '';
          params += `filter.${this.topFilterParamName}=${encodeURIComponent(status)}`;
        });
      }

      if (!params.includes('page=')) {
        params += `&page=${this.page}`;
      }

      if (!params.includes('pageSize=')) {
        params += `&pageSize=${this.pageSize}`;
      }

      this.setActiveFilteringAndValuesByParams(params);

      params = params.replace('filter.submittedOn-code', 'filter.code');
      params = params.replace('submittedOn-code', 'submittedOn');

      this.apiService.getTableData(this.endpoint, params).subscribe(
        (data: any) => {
          this.responseLoaded.emit(data);
          this.pagedResult = this.responseKey ? data[this.responseKey] : data;
          this.applyDraftsFirstOrdering();
          this.updateDynamicFilterOptionsFromSource(data);
          this.updateDynamicFilterOptions();
          this.isAdditionalDataLoading = false;
          setTimeout(() => {
            this.isMainDataLoading = false;
          }, ANIMATION_TIMEOUT_700);
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }

  setActiveFilteringAndValuesByParams(params: string) {
    const sp = new URLSearchParams(params);

    // 1) collect without "filter."
    const raw: Record<string, string[]> = {};
    sp.forEach((value, key) => {
      if (!key.startsWith('filter.')) return;
      const k = key.slice(7);
      (raw[k] ??= []).push(value);
    });

    // 2) search bar
    const s = sp.get('filter.searchText');
    if (s !== null) this.searchValue = s;

    // 3) flatten arrays
    const flat: { key: string; value: string }[] = Object.entries(raw).map(([key, values]) => ({
      key,
      value: values.join('|')
    }));

    // 4) merge From/To → base: "from_to"
    const merged: { key: string; value: string }[] = [];
    const seen = new Set<string>();
    const firstOf = (arr?: string[]) => (arr && arr.length ? arr[0] : '');

    flat.forEach(({ key, value }) => {
      const mFrom = key.match(/^(.*)From$/);
      const mTo = key.match(/^(.*)To$/);

      if (mFrom) {
        const base = mFrom[1];
        if (seen.has(base)) return;
        const to = firstOf(raw[`${base}To`]);
        merged.push({ key: base, value: `${value}_${to ?? ''}` });
        seen.add(base);
        return;
      }
      if (mTo) {
        const base = mTo[1];
        if (seen.has(base)) return;
        const from = firstOf(raw[`${base}From`]);
        merged.push({ key: base, value: `${from ?? ''}_${value}` });
        seen.add(base);
        return;
      }
      if (!seen.has(key)) {
        merged.push({ key, value });
        seen.add(key);
      }
    });

    merged.forEach(({ key: queryKey, value }) => {
      // Resolve which tableFilters key we should write into
      const targetKey = this.resolveTableFilterKeyFromQueryKey(queryKey) || queryKey;
      if (!this.tableFilters[targetKey]) return;

      let finalValue = value;

      // If this param came from a MultiSelect's multiselectFilterName, normalize to valid options
      if (this.isQueryKeyMultiSelectName(queryKey)) {
        finalValue = this.normalizeMultiSelectValue(targetKey, finalValue);
      }

      // If this target is a TimeUtc column, convert UTC from URL → local for display
      if (this.isTimeUtcKey(targetKey)) {
        const [f, t] = finalValue.split('_');
        const lf = this.utcToLocal(f) ?? '';
        const lt = this.utcToLocal(t) ?? '';
        finalValue = `${lf}_${lt}`; // can be "_" if both empty
      }

      this.tableFilters[targetKey].value = finalValue;
    });
  }

  getQueryParams(): URLSearchParams {
    const queryParamMap = this.activatedRoute.snapshot.queryParamMap;
    const params = new URLSearchParams();

    queryParamMap.keys.forEach((key) => {
      const values = queryParamMap.getAll(key);
      values.forEach((value) => {
        params.append(key, value);
      });
    });

    return params;
  }

  getQueryParamsAsString(): string {
    return this.getQueryParams().toString();
  }

  getResolvedInitialColumnFilters(): { [paramName: string]: string[] } {
    const result: { [paramName: string]: string[] } = {};
    Object.keys(this.initialColumnFilters).forEach((filterKey) => {
      const column = this.columns.find((c) => (c.sortKey || c.key) === filterKey);
      if (!column?.multiselectFilterName) return;
      const rawValue = this.tableFilters[filterKey]?.value;
      if (!rawValue) return;
      const values = rawValue.split('|').filter((v: string) => !!v);
      if (values.length === 0) return;
      result[column.multiselectFilterName] = values;
    });
    return result;
  }

  setInitialFilterValues(): void {
    const queryParams = this.activatedRoute.snapshot.queryParams;

    if (queryParams['pageSize']) {
      this.pageSize = Number(queryParams['pageSize']);
    }

    if (queryParams['page']) {
      this.page = Number(queryParams['page']);
    }
  }

  handleAction(id: string, action: string = 'view', row?: any, event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
    }
    if (action === 'auditResult') {
      this.selectedAuditEntry = row;
      this.isAuditEntryModalOpen = true;
    } else if (action === 'view' && this.getViewPathFn && row) {
      this.router.navigate([this.getViewPathFn(row)]);
    } else if (action === 'view' && !this.isEntryPathForSubmittion) {
      this.router.navigate([this.viewEntryPath.replace('{id}', id.toString())]);
    } else if (action === 'view' && this.isEntryPathForSubmittion) {
      row?.initializationStatus === 'InitializationDone'
        ? this.router.navigate([`pieteikums/${row.id}`])
        : this.router.navigate([`pakalpojumi/lasitavas-apmeklejums/${row.id}`]);
    } else if (action === 'edit') {
      this.router.navigate([this.editEntryPath.replace('{id}', id.toString())]);
    } else if (action === 'read') {
      this.apiService.markAsReadUserNotification(id).subscribe(
        () => {
          this.getTableData();
          this.notificationService.markAsRead(id);
        },
        (error: any) => {
          console.error('Error loading classifier values:', error);
        }
      );
    } else if (action === 'unread') {
      this.apiService.markAsReadUserNotification(id, false).subscribe(
        () => {
          this.getTableData();
          this.notificationService.markAsUnread(id);
        },
        (error: any) => {
          console.error('Error loading classifier values:', error);
        }
      );
    }
  }

  getTaskDateStatus(row: any): 'overdue' | 'approaching' | 'normal' {
    const status = row['status'];
    if (status === 'Completed' || status === 'Cancelled') return 'normal';
    const dateStr = row['executionDate'];
    if (!dateStr || dateStr === '0001-01-01T00:00:00') return 'normal';
    const parts = dateStr.substring(0, 10).split('-');
    const execDate = new Date(+parts[0], +parts[1] - 1, +parts[2]);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (execDate < today) return 'overdue';
    if (execDate.getTime() === today.getTime()) return 'approaching';
    return 'normal';
  }

  emitCustomAction(action: string, row: any, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.customActionClicked.emit({ action, row });
  }

  sort(column: TableColumn) {
    if (this.sortBy === column.sortKey || (!column.sortKey && this.sortBy === column.key)) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = column.sortKey || column.key;
      this.sortDirection = 'desc';
    }

    this.router.navigate([], {
      queryParams: {
        'sortBy.by': this.sortBy,
        'sortBy.desc': this.sortDirection === 'desc',
        sorts: this.sortBy,
        page: this.page
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  handleKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.search();
    }
  }

  search(): void {
    this.page = 1;
    this.router.navigate([], {
      queryParams: {
        'filter.searchText': this.searchValue,
        page: this.page
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  clearSearch(): void {
    this.searchValue = '';
    this.search();
  }

  getClassName(key: string | boolean = ''): string {
    if (key === 'RequestedAdditionalInformation') return 'requested-additional-information';
    if (key === 'Approved') return 'approved';
    if (key === 'Cancelled') return 'canceled';
    if (key === 'Rejected') return 'rejected';
    if (key === 'Draft') return 'draft';
    if (key === 'Active') return 'active';
    if (key === 'Blocked') return 'inactive';
    if (key === 'New') return 'new';
    if (key === 'Unknown') return 'unknown';
    if (key === 'NewRedirected') return 'new-redirected';
    if (key === 'InProcess') return 'in-process';
    if (key === 'SubmittedAdditionalInformation') return 'submitted-additional-information';
    if (key === 'Organization') return 'organization';
    if (key === 'Individual') return 'individual';
    if (key === 'Unfinished') return 'unfinished';
    if (key === 'Deleted') return 'deleted';
    if (key === 'Registered') return 'new';
    if (key === 'InProcessing') return 'ir-in-processing';
    if (key === 'Calculation') return 'in-process';
    if (key === 'AwaitingPayment') return 'ir-awaiting-payment';
    if (key === 'Paid') return 'approved';
    if (key === 'Namejs') return 'in-process';
    if (key === 'DigitallySigned') return 'approved';
    if (key === 'Completed') return 'approved';
    if (key === 'NewPortal') return 'new';
    if (key === 'NewBackoffice') return 'new';
    if (key === 'PreliminaryResearch') return 'ir-preliminary-research';
    if (key === 'AdditionalInfoRequested') return 'ir-additional-info-requested';
    if (key === 'AdditionalInfoSubmitted') return 'ir-additional-info-submitted';
    // Translated (Latvian) status values from /api/service-request
    if (key === 'Jauns') return 'new';
    if (key === 'Reģistrēts') return 'new';
    if (key === 'Aprēķins') return 'in-process';
    if (key === 'Gaida apmaksu') return 'ir-awaiting-payment';
    if (key === 'Apmaksāts') return 'approved';
    if (key === 'Digitāli parakstīts') return 'approved';
    if (key === 'Izpildīts') return 'approved';
    if (key === 'Atcelts') return 'canceled';
    if (key === 'Anulēts') return 'canceled';
    if (key === 'Melnraksts') return 'draft';
    if (key === 'Jauns (Portāls)') return 'new';
    if (key === 'Jauns (BO)') return 'new';
    if (key === 'Jauns (Pārvirzīts)') return 'new-redirected';
    if (key === 'Jauns (pārvirzīts)') return 'new-redirected';
    if (key === 'Priekšizpēte') return 'ir-preliminary-research';
    if (key === 'Pieprasītas papildziņas') return 'ir-additional-info-requested';
    if (key === 'Iesniegtas papildziņas') return 'ir-additional-info-submitted';
    if (key === 'Apstrādē') return 'ir-in-processing';
    if (key === 'Apstiprināts') return 'approved';
    if (key === 'Noraidīts') return 'rejected';
    if (key === 'Atsaukts') return 'canceled';
    if (key === 'Nepabeigts') return 'unfinished';
    // Task statuses
    if (key === 'Waiting') return 'waiting';
    if (key === 'Assigned') return 'assigned';
    if (key === 'Accepted') return 'accepted';
    if (key === true) return 'read';
    if (key === false) return 'unread';
    return '';
  }

  openOverlayKey: string | null = null;

  toggleFilterOverlay(column: TableColumn): void {
    const key = column.sortKey || column.key;

    if (this.tableFilters[key].isOpen) {
      this.tableFilters[key].isOpen = false;
      this.openOverlayKey = null; // <- clear
    } else {
      Object.keys(this.tableFilters).forEach((k) => (this.tableFilters[k].isOpen = false));
      this.tableFilters[key].isOpen = true;
      this.openOverlayKey = key; // <- remember which overlay is open

      const overlayId = column.sortKey ? `filter-overlay-${column.sortKey}` : `filter-overlay-${column.key}`;
      const headerId = `header-${column.sortKey || column.key}`;

      const overlayElement = document.getElementById(overlayId);
      const headerElement = document.getElementById(headerId);

      if (overlayElement && headerElement) {
        const headerRectangle = headerElement.getBoundingClientRect();
        overlayElement.style.left = `${headerRectangle.x + HEADER_PADDING}px`;
        overlayElement.style.top = `${headerRectangle.y + headerRectangle.height - HEADER_PADDING}px`;
      }

      setTimeout(() => {
        const overlayElement = document.getElementById(overlayId);
        const tableRect = this.tableWrapper.nativeElement.getBoundingClientRect();
        if (overlayElement) {
          const overlayRect = overlayElement.getBoundingClientRect();
          if (overlayRect.right > tableRect.right) {
            overlayElement.style.left = `${tableRect.right - overlayRect.width}px`;
          }
        }
      }, 1);
    }
  }

  handleTimeRangeFilter(column: TableColumn, data: any) {
    const key = column.sortKey || column.key;
    const from = this.normalizePart(data?.from) || '';
    const to = this.normalizePart(data?.to) || '';
    this.tableFilters[key].value = !from && !to ? '' : `${from}_${to}`;
  }

  handleMultiSelectFilter(column: TableColumn, value: string = 'all'): void {
    const key = column.sortKey || column.key;
    if (value === 'all') {
      if (this.areAllMultiSelectOptionsSelected(key)) {
        this.tableFilters[key].value = '';
      } else {
        const filterOptions = this.columns.find((col: TableColumn) => (col.sortKey || col.key) === key)?.filterOptions || [];
        this.tableFilters[key].value = filterOptions.join('|');
      }
    } else {
      const selected = this.tableFilters[key].value ? this.tableFilters[key].value.split('|') : [];
      const index = selected.indexOf(value);
      if (index > -1) {
        selected.splice(index, 1);
      } else {
        selected.push(value);
      }
      this.tableFilters[key].value = selected.join('|');
    }
    if (this.topFilterParamName && column.multiselectFilterName === this.topFilterParamName) {
      this.syncColumnFilterToTopFilter();
    }
  }

  isFilterChecked(column: TableColumn, value: string): boolean {
    if (value === 'all') {
      return this.areAllMultiSelectOptionsSelected(column.sortKey || column.key);
    }

    const selected = this.tableFilters[column.sortKey || column.key].value.split('|');
    return selected.includes(value);
  }

  areAllMultiSelectOptionsSelected(key: string): boolean {
    const options = this.columns.filter((column: TableColumn) => column.sortKey === key || column.key === key)[0].filterOptions || [];
    const selectedOptions = this.tableFilters[key].value ? this.tableFilters[key].value.split('|').filter((v) => !!v) : [];

    return options.length > 0 && options.length === selectedOptions.length;
  }

  onDateRangeChange(data: any, column: TableColumn, dateFilterKey?: string) {
    let fromDate = '';
    let toDate = '';
    const targetKey = dateFilterKey || column.sortKey || column.key;
    const useTimeRange = column.filter === FilterType.DateTime || !!dateFilterKey;

    // Helper to format timezone offset like "+03:00" or "-05:30"
    const getTimezoneOffset = () => {
      const offsetMinutes = -new Date().getTimezoneOffset(); // reverse the sign
      const sign = offsetMinutes >= 0 ? '+' : '-';
      const pad = (n: number) => String(Math.floor(Math.abs(n))).padStart(2, '0');
      const hours = pad(offsetMinutes / 60);
      const minutes = pad(offsetMinutes % 60);
      return `${sign}${hours}:${minutes}`;
    };

    const timezoneOffset = getTimezoneOffset();

    if (data.from) {
      fromDate = `${data.from}`;
      if (useTimeRange) {
        fromDate = `${data.from}T00:00:00${timezoneOffset}`;
      }
    }

    if (data.to) {
      toDate = `${data.to}`;
      if (useTimeRange) {
        toDate = `${data.to}T23:59:59${timezoneOffset}`;
      }
    }

    if (!data.from && !data.to) {
      this.tableFilters[targetKey].value = '';
    } else {
      this.tableFilters[targetKey].value = `${fromDate}_${toDate}`;
    }
  }

  formatNgbDate(date: { year: number; month: number; day: number }): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(date.day)}.${pad(date.month)}.${date.year}`;
  }

  applyFilter(column: TableColumn): void {
    const filterKeys = Object.keys(this.tableFilters);
    this.page = 1;

    const queryParams: any = {
      page: this.page
    };

    filterKeys.forEach((filterKey: string) => {
      const filterType: FilterType =
        this.columns.find((column: TableColumn) => column.sortKey === filterKey || column.key === filterKey)?.filter ||
        (this.columns.some((c: TableColumn) => c.dateFilterKey === filterKey) ? FilterType.DateTime : FilterType.Search);

      const value = this.tableFilters[filterKey]?.value;

      if (!value?.length) {
        return;
      }

      if (filterType === FilterType.SearchIdentityNumber) {
        queryParams[`filter.${filterKey}`] = value;
        return;
      }

      const column = this.columns.find((c) => c.key === filterKey || c.sortKey === filterKey);

      if (filterType === FilterType.Date || filterType === FilterType.DateTime) {
        const [from, to] = value.split('_');

        // Check for special logic
        if (column?.customLogic === 'activeAbsence') {
          if (from) {
            queryParams['filter.ActiveAbsenceDatePeriodStartFrom'] = from;
          }
          if (to) {
            queryParams['filter.ActiveAbsenceDatePeriodEndTo'] = to;
          }
        } else {
          if (from) {
            queryParams[`filter.${filterKey}From`] = from;
          }
          if (to) {
            queryParams[`filter.${filterKey}To`] = to;
          }
        }

        return;
      }

      if (filterType === FilterType.Time) {
        const range = this.tableFilters[filterKey].value;
        const [fromStr, toStr] = range.split('_');
        if (fromStr !== 'null') {
          queryParams[`filter.${filterKey}From`] = fromStr;
        }
        if (toStr !== 'null') {
          queryParams[`filter.${filterKey}To`] = toStr;
        }

        return;
      }

      if (filterType === FilterType.TimeUtc) {
        const [fromStr, toStr] = value.split('_');

        const convertLocalTimeToUTC = (timeStr: string): string | null => {
          if (!timeStr || !timeStr.includes(':')) return null;
          const [hoursStr, minutesStr] = timeStr.split(':');
          const hours = Number(hoursStr);
          const minutes = Number(minutesStr);
          if (isNaN(hours) || isNaN(minutes)) return null;

          const localDate = new Date();
          localDate.setHours(hours, minutes, 0, 0);
          const utcHours = localDate.getUTCHours().toString().padStart(2, '0');
          const utcMinutes = localDate.getUTCMinutes().toString().padStart(2, '0');
          return `${utcHours}:${utcMinutes}`;
        };

        const fromUtc = convertLocalTimeToUTC(fromStr);
        const toUtc = convertLocalTimeToUTC(toStr);
        if (fromUtc) queryParams[`filter.${filterKey}From`] = fromUtc;
        if (toUtc) queryParams[`filter.${filterKey}To`] = toUtc;
        return;
      }

      if (column?.filter === FilterType.MultiSelect && column.multiselectFilterName) {
        // Skip columns managed by topFilter to avoid URL/topFilter conflict
        if (this.topFilterParamName && column.multiselectFilterName === this.topFilterParamName) {
          return;
        }
        const selectedValues = value.split('|').filter((v) => !!v);
        const paramKey = `filter.${column.multiselectFilterName}`;
        const allOptions = column.filterOptions ?? [];

        if (selectedValues.length === allOptions.length) {
          delete queryParams[paramKey];
        } else if (selectedValues.length) {
          queryParams[paramKey] = selectedValues;
        } else {
          delete queryParams[paramKey];
        }
      } else {
        if (filterKey === 'submittedOn-code') {
          queryParams['filter.code'] = value;
        } else if (column?.searchFilterName) {
          queryParams[`filter.${column.searchFilterName}`] = value;
        } else {
          queryParams[`filter.${filterKey}`] = value;
        }
      }
    });

    if (this.useLocalSrorage && this.localStorageKey) {
      this.saveFiltersToLocalStorage(queryParams);
    } else {
      const existingParams = this.activatedRoute.snapshot.queryParams;

      if (existingParams['activeSection']) {
        queryParams['activeSection'] = existingParams['activeSection'];
      }

      this.router
        .navigate([], {
          queryParams,
          replaceUrl: true
        })
        .then(() => {
          this.getTableData();
        });
    }

    if (column) {
      this.toggleFilterOverlay(column);
    }
  }

  removeFilter(column: TableColumn): void {
    const key = column.sortKey || column.key;

    if (!this.tableFilters.hasOwnProperty(key)) {
      return;
    }

    const filter = this.tableFilters[key];
    filter.value = '';

    if (
      column.filter === FilterType.Date ||
      column.filter === FilterType.Time ||
      column.filter === FilterType.TimeUtc ||
      column.filter === FilterType.DateTime
    ) {
      filter.resetTrigger = true;
      setTimeout(() => (filter.resetTrigger = false));
    }

    if (column.dateFilterKey && this.tableFilters[column.dateFilterKey]) {
      const dateFilter = this.tableFilters[column.dateFilterKey];
      dateFilter.value = '';
      dateFilter.resetTrigger = true;
      setTimeout(() => (dateFilter.resetTrigger = false));
    }

    if (this.topFilterParamName && column.multiselectFilterName === this.topFilterParamName) {
      this.topFilterSelected = new Set();
    }

    this.applyFilter(column);
  }

  handlePageChange(): void {
    const queryParams = {
      page: this.page,
      sorts: this.sortBy
    };

    if (this.useLocalSrorage && this.localStorageKey) {
      this.saveFiltersToLocalStorage(queryParams);
    }

    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private saveFiltersToLocalStorage(queryParams: any): void {
    const existingFilters = localStorage.getItem(`tableFilters_${this.localStorageKey}`);
    const updatedFilters = existingFilters ? JSON.parse(existingFilters) : {};

    Object.assign(updatedFilters, queryParams);
    localStorage.setItem(`tableFilters_${this.localStorageKey}`, JSON.stringify(updatedFilters));
  }

  onPageSizeChange(pageSize: Event) {
    const selectedValue = Number((pageSize.target as HTMLSelectElement).value); // Get the selected value
    this.pageSize = selectedValue;
    this.page = 1;
    const queryParams = {
      pageSize: this.pageSize,
      page: this.page
    };

    if (this.useLocalSrorage && this.localStorageKey) {
      this.saveFiltersToLocalStorage(queryParams);
    }

    this.router.navigate([], {
      queryParams: queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  getBodyHeight() {
    return this.tableBody.nativeElement.offsetHeight;
  }

  getSearchPlaceholder(): string {
    if (this.type === 'clientList') {
      return 'components.table.searchClient';
    }

    if (this.type === 'orgClientList') {
      return 'components.table.searchOrgClient';
    }

    return 'components.table.search';
  }

  isFilterActive(column: TableColumn): boolean {
    const hasValue = (key?: string): boolean => {
      const value = key ? this.tableFilters[key]?.value : '';
      if (!value || value === '_') return false;
      // '-' is a legitimate multi-select option (the empty/unassigned sentinel, LNA-1957);
      // for other filter types a lone '-' still counts as no filter.
      if (value === '-' && column.filter !== FilterType.MultiSelect) return false;
      return true;
    };
    return hasValue(column.sortKey || column.key) || hasValue(column.dateFilterKey);
  }

  getCellClassName(column: TableColumn, row: any): string {
    return this.getClassName(this.getCellValue(row, column.key));
  }

  getTranslateKey(column: TableColumn, row: any): string {
    const value = this.getCellValue(row, column.key);
    if (value === null || value === undefined || value === '' || value === '-') return '-';
    return `${column.valueTranslate}${value}`;
  }

  getCellValue(row: any, key: string): any {
    try {
      const column = this.columns.find((c) => c.key === key);

      if (column?.hideValues?.includes(row[key])) return '-';

      if (column?.legacyValueFrom && !row[key]) {
        const legacyValue = row[column.legacyValueFrom.key];
        if (column.legacyValueFrom.values.includes(legacyValue)) return legacyValue;
      }

      if (column && column.type === 'time') {
        const rawValue = row[key];
        if (!rawValue) return '-';

        const utcTime = rawValue.replace(' UTC', '');
        const [hours, minutes] = utcTime.split(':').map(Number);

        const localDate = new Date();
        localDate.setUTCHours(hours, minutes, 0, 0);

        const localHours = localDate.getHours().toString().padStart(2, '0');
        const localMinutes = localDate.getMinutes().toString().padStart(2, '0');

        return `${localHours}:${localMinutes}`;
      }

      if (column && column.filter === FilterType.Time) {
        return row[key].slice(0, TIME_FORMAT.length);
      }

      const parts = key.split('.');
      if (parts.length === 1) {
        return row[parts[0]] ?? '-';
      }

      let value = row;
      parts.forEach((part: string) => {
        value = value[part];
      });
      return value ?? '-';
    } catch (error) {
      return '-';
    }
  }

  getImageUrl(row: any): string {
    if (this.images[row['outerId']]) {
      return this.images[row['outerId']];
    } else if (row['outerId'] && this.images[row['outerId']] != '') {
      this.displayImage(row['outerId']);
    }
    return '';
  }

  displayImage(fileOuterId: string): void {
    this.images[fileOuterId] = '';
    this.apiService.downloadFile(fileOuterId).subscribe((data) => {
      const blob = new Blob([data], { type: 'image/jpeg' });
      this.images[fileOuterId] = window.URL.createObjectURL(blob);
    });
  }

  getJsonValue(row: any, column: TableColumn): string {
    const parsedJson = JSON.parse(row['createdBy']);

    if (parsedJson === null || !parsedJson) {
      return '-';
    }

    if (column.customLogic === 'jsonUserName') {
      return parsedJson.UserName;
    } else if (column.customLogic === 'jsonIpAddress') {
      return parsedJson.IpAddress.length > 0 ? parsedJson.IpAddress : '-';
    } else if (column.customLogic === 'jsonSystem') {
      return parsedJson.System;
    } else {
      return '-';
    }
  }

  handleOverlayEnter(column: TableColumn, event?: Event): void {
    event?.preventDefault();
    this.applyFilter(column);
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(e: KeyboardEvent) {
    if (e.key !== 'Enter') return;
    if (!this.openOverlayKey) return;

    e.preventDefault();

    const col = this.columns.find((c) => (c.sortKey || c.key) === this.openOverlayKey);
    if (col) this.applyFilter(col);
  }

  private normalizePart(s: string | null | undefined): string | null {
    if (!s) return null;
    const v = String(s).trim();
    return v === 'null' || v === 'undefined' || v === '_' || v === '-' ? null : v;
  }

  private utcToLocal(hhmm: string | null | undefined): string | null {
    if (!hhmm || !hhmm.includes(':')) return null;
    const [h, m] = hhmm.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    const d = new Date();
    d.setUTCHours(h, m, 0, 0);
    const lh = String(d.getHours()).padStart(2, '0');
    const lm = String(d.getMinutes()).padStart(2, '0');
    return `${lh}:${lm}`;
  }

  private isTimeUtcKey(key: string): boolean {
    const col = this.columns.find((c) => (c.sortKey || c.key) === key);
    return col?.filter === FilterType.TimeUtc;
  }

  private getTimeRangeParts(column: TableColumn): { from: string | null; to: string | null } {
    const key = column.sortKey || column.key;
    const raw = this.tableFilters[key]?.value || '';
    const [fromRaw, toRaw] = raw.split('_');
    return {
      from: this.normalizePart(fromRaw),
      to: this.normalizePart(toRaw)
    };
  }

  getTimeFrom(column: TableColumn): string | null {
    return this.getTimeRangeParts(column).from;
  }

  getTimeTo(column: TableColumn): string | null {
    return this.getTimeRangeParts(column).to;
  }

  private resolveTableFilterKeyFromQueryKey(queryKey: string): string | null {
    const col = this.columns.find(
      (c) =>
        (c.sortKey || c.key) === queryKey ||
        (c.filter === FilterType.MultiSelect && c.multiselectFilterName === queryKey) ||
        c.searchFilterName === queryKey
    );
    return col ? col.sortKey || col.key : null;
  }

  // Is the query key a multiselectFilterName (not the column key)?
  private isQueryKeyMultiSelectName(queryKey: string): boolean {
    return this.columns.some((c) => c.filter === FilterType.MultiSelect && c.multiselectFilterName === queryKey);
  }

  private applyDraftsFirstOrdering(): void {
    if (!this.draftsFirstKey || !this.pagedResult?.list?.length) return;
    const key = this.draftsFirstKey;
    const list = this.pagedResult.list as Record<string, unknown>[];
    const drafts = list.filter((row) => row[key] === null || row[key] === undefined || row[key] === '');
    const rest = list.filter((row) => row[key] !== null && row[key] !== undefined && row[key] !== '');
    this.pagedResult = { ...this.pagedResult, list: [...drafts, ...rest] };
  }

  private updateDynamicFilterOptionsFromSource(data: any): void {
    for (const column of this.columns) {
      if (!column.dynamicFilterSourceField) continue;
      const sourceItems: any[] = data[column.dynamicFilterSourceField];
      if (!Array.isArray(sourceItems) || !sourceItems.length) continue;

      const colKey = column.sortKey || column.key;
      const idKey = column.dynamicFilterSourceIdKey || 'id';
      const labelKey = column.dynamicFilterSourceLabelKey || 'fullName';

      if (!this.dynamicFilterOptionsAccumulator.has(colKey)) {
        this.dynamicFilterOptionsAccumulator.set(colKey, new Set<string>());
      }
      const accumulator = this.dynamicFilterOptionsAccumulator.get(colKey)!;
      if (!column.filterOptionLabels) column.filterOptionLabels = new Map();

      for (const item of sourceItems) {
        if (typeof item === 'string') {
          if (item !== '') accumulator.add(item);
        } else {
          const id = item[idKey];
          const label = item[labelKey];
          if (id != null && id !== '') {
            accumulator.add(id);
            if (label) column.filterOptionLabels.set(id, label);
          }
        }
      }

      column.filterOptions = Array.from(accumulator).sort((a, b) => {
        // Pin the "-" (empty/unassigned) sentinel to the top of the list
        if (a === '-') return -1;
        if (b === '-') return 1;
        return (column.filterOptionLabels!.get(a) || a).localeCompare(column.filterOptionLabels!.get(b) || b, 'lv');
      });
    }
  }

  private updateDynamicFilterOptions(): void {
    if (!this.pagedResult?.list?.length) return;

    for (const column of this.columns) {
      if (!column.dynamicFilterOptionsField) continue;

      const field = column.dynamicFilterOptionsField;
      const idField = column.dynamicFilterIdField;
      const colKey = column.sortKey || column.key;

      if (!this.dynamicFilterOptionsAccumulator.has(colKey)) {
        this.dynamicFilterOptionsAccumulator.set(colKey, new Set<string>());
      }

      const accumulator = this.dynamicFilterOptionsAccumulator.get(colKey)!;

      if (idField) {
        if (!column.filterOptionLabels) column.filterOptionLabels = new Map();
        for (const row of this.pagedResult.list) {
          const id = row[idField];
          const label = row[field];
          if (id != null && id !== '') {
            accumulator.add(id);
            if (label) column.filterOptionLabels.set(id, label);
          }
        }
      } else {
        for (const row of this.pagedResult.list) {
          const value = row[field];
          if (Array.isArray(value)) {
            value.forEach((v: string) => {
              if (v != null && v !== '') accumulator.add(v);
            });
          } else if (value != null && value !== '') {
            accumulator.add(value);
          }
        }
      }

      column.filterOptions = Array.from(accumulator).sort((a, b) => {
        // Pin the "-" (empty/unassigned) sentinel to the top of the list
        if (a === '-') return -1;
        if (b === '-') return 1;
        if (column.filterOptionLabels) {
          return (column.filterOptionLabels.get(a) || a).localeCompare(column.filterOptionLabels.get(b) || b, 'lv');
        }
        return a.localeCompare(b, 'lv');
      });
    }
  }

  getArrayJoinValue(row: any, key: string): string {
    const val = this.getCellValue(row, key);
    return Array.isArray(val) ? (val.length ? val.join(', ') : '-') : val || '-';
  }

  getAssigneeWithCodes(row: any): string {
    if (row.assigneeUserFullName) {
      const codes = row.assigneeUserStructuralUnitCodes;
      if (codes?.length) {
        return `${row.assigneeUserFullName} (${codes.join(', ')})`;
      }
      return row.assigneeUserFullName;
    }
    if (row.assigneeStructuralUnitCode) {
      return row.assigneeStructuralUnitCode;
    }
    return row.assigneeStructuralUnitName || '';
  }

  toggleTopFilter(status: string): void {
    const next = new Set(this.topFilterSelected);
    if (next.has(status)) {
      next.delete(status);
    } else {
      next.add(status);
    }
    this.topFilterSelected = next;
    this.syncTopFilterToColumnFilter();
    this.getTableData();
  }

  selectAllTopFilters(): void {
    this.topFilterSelected = new Set(this.topFilterOptions);
    this.syncTopFilterToColumnFilter();
    this.getTableData();
  }

  deselectAllTopFilters(): void {
    this.topFilterSelected = new Set();
    this.syncTopFilterToColumnFilter();
    this.getTableData();
  }

  private syncTopFilterToColumnFilter(): void {
    if (!this.topFilterParamName) return;
    const col = this.columns.find((c) => c.multiselectFilterName === this.topFilterParamName);
    if (!col) return;
    const key = col.sortKey || col.key;
    if (this.tableFilters[key]) {
      this.tableFilters[key].value = [...this.topFilterSelected].join('|');
    }
  }

  private syncColumnFilterToTopFilter(): void {
    if (!this.topFilterParamName) return;
    const col = this.columns.find((c) => c.multiselectFilterName === this.topFilterParamName);
    if (!col) return;
    const key = col.sortKey || col.key;
    const value = this.tableFilters[key]?.value || '';
    const selected = value.split('|').filter((v) => !!v && this.topFilterOptions.includes(v));
    this.topFilterSelected = new Set(selected);
  }

  getTopFilterCount(status: string): number {
    return this.topFilterCounts.find((c) => c.status === status)?.count ?? 0;
  }

  // Keep only allowed options for a given MultiSelect column (safety / cleanliness)
  private normalizeMultiSelectValue(tableFilterKey: string, rawValue: string): string {
    const col = this.columns.find((c) => (c.sortKey || c.key) === tableFilterKey);
    if (!col) return rawValue;
    if (col.dynamicFilterOptionsField && (!col.filterOptions || col.filterOptions.length === 0)) {
      return rawValue;
    }
    const allowed = new Set(col.filterOptions || []);
    return rawValue
      .split('|')
      .filter((v) => !!v && allowed.has(v))
      .join('|');
  }
}
